import { graphemeSegmenter } from "./grapheme-segmenter";

/**
 * X (Twitter) weighted character counting — shared by the X provider's
 * validate/publish path and the composer's character ring so the UI matches
 * what the API enforces.
 */

const URL_WEIGHT = 23;
// X wraps scheme-less `www.` links in t.co too — both weigh 23.
const urlPattern = /(?:https?:\/\/|www\.)\S+/gi;

/** CJK ranges count as 2 in X's weighted length. */
function isWideCodePoint(cp: number): boolean {
  return (
    (cp >= 0x1100 && cp <= 0x115f) || // Hangul Jamo
    (cp >= 0x2e80 && cp <= 0x303e) || // CJK radicals / symbols
    (cp >= 0x3041 && cp <= 0x33ff) || // Hiragana … CJK compat
    (cp >= 0x3400 && cp <= 0x4dbf) || // CJK Ext A
    (cp >= 0x4e00 && cp <= 0x9fff) || // CJK Unified
    (cp >= 0xa000 && cp <= 0xa4cf) || // Yi
    (cp >= 0xac00 && cp <= 0xd7a3) || // Hangul syllables
    (cp >= 0xf900 && cp <= 0xfaff) || // CJK compat ideographs
    (cp >= 0xff00 && cp <= 0xff60) || // Fullwidth forms
    (cp >= 0xffe0 && cp <= 0xffe6) ||
    cp >= 0x1f000 // Non-emoji pictographic planes (emoji handled per grapheme)
  );
}

/**
 * X counts every emoji grapheme as 2 — including BMP emoji (❤, ✂), keycaps,
 * flags (regional-indicator pairs), and ZWJ sequences.
 */
const emojiGraphemePattern =
  /\p{Extended_Pictographic}|\p{Regional_Indicator}|\u{FE0F}|\u{20E3}/u;

/**
 * X's weighted post length: every URL counts as 23 (t.co wrapping), every
 * emoji grapheme counts as 2 (matching twitter-text, which weights all emoji
 * as 2 regardless of code-point makeup), CJK code points count as 2,
 * everything else as 1. Pre-flight UX guard — the X API is authoritative.
 */
export function weightedTweetLength(text: string): number {
  let weight = 0;
  const withoutUrls = text.replace(urlPattern, () => {
    weight += URL_WEIGHT;
    return "";
  });
  for (const { segment } of graphemeSegmenter().segment(withoutUrls)) {
    if (emojiGraphemePattern.test(segment)) {
      weight += 2;
      continue;
    }
    for (const char of segment) {
      weight += isWideCodePoint(char.codePointAt(0) ?? 0) ? 2 : 1;
    }
  }
  return weight;
}

/**
 * TLDs X reliably t.co-wraps when written without a scheme. Deliberately a
 * curated list rather than "any dotted token": billing treats a wrapped link as
 * a $0.20 post instead of $0.015, so this decides real money — but matching
 * every `word.word` would bill `notes.txt` or `v1.2` as links and silently eat
 * a user's X budget. Add TLDs here as real posts need them.
 */
const linkTlds = new Set([
  "ai",
  "app",
  "biz",
  "blog",
  "buzz",
  "cc",
  "click",
  "club",
  "co",
  "com",
  "de",
  "dev",
  "digital",
  "edu",
  "es",
  "eu",
  "fm",
  "fr",
  "gg",
  "gov",
  "gl",
  "icu",
  "in",
  "info",
  "io",
  "it",
  "jp",
  "link",
  "live",
  "ly",
  "me",
  "media",
  "mobi",
  "net",
  "news",
  "nl",
  "online",
  "org",
  "page",
  "pro",
  "pw",
  "ru",
  "shop",
  "site",
  "space",
  "store",
  "stream",
  "to",
  "top",
  "tv",
  "uk",
  "us",
  "vip",
  "website",
  "work",
  "xyz",
]);

/**
 * A scheme-less domain (`example.com/path`, `sub.example.io`). Anchored on a
 * word boundary and validated against `linkTlds` below so version strings and
 * filenames don't match.
 */
const bareDomainPattern =
  /\b(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+([a-z]{2,})(?=[/?#]|\b)/gi;

/**
 * Whether X would t.co-wrap something in this text — i.e. whether the post
 * counts as "containing a URL" for pay-per-use billing ($0.20 vs $0.015).
 *
 * Broader than the length-weighting pattern on purpose: that one only needs
 * `https://`/`www.` links to get the 23-char weight right, but under-detecting
 * here means we bill a user 15 credits for a post that costs us 200. When in
 * doubt this errs toward "yes, it's a link".
 */
export function containsUrl(text: string): boolean {
  // Fresh non-global regex: `urlPattern` carries the /g flag, whose stateful
  // lastIndex makes `.test()` alternate true/false across calls.
  if (/(?:https?:\/\/|www\.)\S+/i.test(text)) {
    return true;
  }

  bareDomainPattern.lastIndex = 0;
  let match = bareDomainPattern.exec(text);
  while (match) {
    if (linkTlds.has((match[1] ?? "").toLowerCase())) {
      return true;
    }
    match = bareDomainPattern.exec(text);
  }

  return false;
}
